import React, { useEffect, useState, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import axiosClient from "../api/axiosClient";

const API = "https://api.viralgpt.com/admin/dunning";

const card = {
  background: "#fff", borderRadius: "16px", border: "1px solid #e3e8ee",
  boxShadow: "0 2px 4px rgba(50,50,93,0.06)", padding: "1.5rem", marginBottom: "1.5rem",
};
const label = {
  fontSize: "0.8rem", fontWeight: 600, color: "#425466", textTransform: "uppercase",
  letterSpacing: "0.08em", marginBottom: "0.5rem",
};
const bigNum = {
  fontSize: "2rem", fontWeight: 700, color: "#0a2540", lineHeight: 1.2,
};

const statusColors = {
  active: { bg: "#fff8e1", color: "#f59e0b", label: "Active" },
  recovered: { bg: "#e8f5e9", color: "#22c55e", label: "Recovered" },
  exhausted: { bg: "#fce4ec", color: "#ef4444", label: "Exhausted" },
  hard_decline: { bg: "#fce4ec", color: "#dc2626", label: "Hard Decline" },
};

const attemptStatusColors = {
  success: "#22c55e",
  failed: "#ef4444",
  pending: "#f59e0b",
  skipped: "#94a3b8",
};

function Badge({ status, map }) {
  const s = map[status] || { bg: "#f1f5f9", color: "#64748b", label: status };
  return (
    <span style={{
      display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: "6px",
      fontSize: "0.75rem", fontWeight: 600, background: s.bg, color: s.color,
    }}>
      {s.label || status}
    </span>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

const DunningDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [activeCases, setActiveCases] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ov, ac, hi] = await Promise.all([
        axiosClient.get(`${API}/overview`),
        axiosClient.get(`${API}/active`),
        axiosClient.get(`${API}/history?limit=30`),
      ]);
      setOverview(ov.data);
      setActiveCases(ac.data);
      setHistory(hi.data);
    } catch (err) {
      console.error("Error fetching dunning data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRetry = async (invoiceId) => {
    setActionLoading(invoiceId);
    try {
      await axiosClient.post(`${API}/${invoiceId}/retry`);
      await fetchAll();
    } catch (err) {
      console.error("Retry failed:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (invoiceId) => {
    if (!window.confirm("Cancel dunning and terminate subscription?")) return;
    setActionLoading(invoiceId);
    try {
      await axiosClient.post(`${API}/${invoiceId}/cancel`);
      await fetchAll();
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin" style={{ color: "#635bff", fontSize: "1.25rem" }}></i>
        <p className="mt-2" style={{ color: "#425466" }}>Loading dunning data...</p>
      </div>
    );
  }

  const ov = overview || {};

  return (
    <div>
      {/* Summary Cards */}
      <Row className="mb-4">
        {[
          { label: "Active Cases", value: ov.active || 0, icon: "fa-clock", color: "#f59e0b" },
          { label: "Recovered (7d)", value: ov.recoveredThisWeek || 0, icon: "fa-check-circle", color: "#22c55e",
            sub: ov.recoveredRevenueThisWeek ? `$${Number(ov.recoveredRevenueThisWeek).toFixed(0)} recovered` : null },
          { label: "Total Recovered", value: ov.totalRecovered || 0, icon: "fa-heart", color: "#635bff" },
          { label: "Recovery Rate", value: `${ov.recoveryRate || 0}%`, icon: "fa-chart-line", color: "#0ea5e9" },
        ].map((s, i) => (
          <Col md={3} sm={6} key={i}>
            <div style={card}>
              <div style={label}>
                <i className={`fas ${s.icon} me-1`} style={{ color: s.color }}></i> {s.label}
              </div>
              <div style={bigNum}>{s.value}</div>
              {s.sub && <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "0.25rem" }}>{s.sub}</div>}
            </div>
          </Col>
        ))}
      </Row>

      {/* Active Dunning Cases */}
      <div style={card}>
        <div style={{ ...label, marginBottom: "1rem" }}>Active Dunning Cases ({activeCases.length})</div>
        {activeCases.length === 0 ? (
          <p style={{ color: "#64748b", textAlign: "center", padding: "2rem 0" }}>No active dunning cases</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e3e8ee" }}>
                  {["Email", "Plan", "Amount", "Fail Reason", "Tier", "Attempt", "Next Retry", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "0.6rem 0.75rem", color: "#425466", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeCases.map((c) => (
                  <tr key={c.invoiceid} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "0.6rem 0.75rem" }}>{c.email}</td>
                    <td style={{ padding: "0.6rem 0.75rem", whiteSpace: "nowrap" }}>
                      {c.planName} ({c.planInterval})
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600 }}>${c.price}</td>
                    <td style={{ padding: "0.6rem 0.75rem", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={`${c.fail_code || ""} — ${c.fail_reason || ""}`}>
                      {c.fail_code && <span style={{ color: "#ef4444", fontWeight: 600, marginRight: "0.3rem" }}>{c.fail_code}</span>}
                      {c.fail_reason || "—"}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", padding: "0.15rem 0.5rem", borderRadius: "4px",
                        fontSize: "0.75rem", fontWeight: 700,
                        background: c.retry_tier === "B" ? "#fef3c7" : "#ede9fe", color: c.retry_tier === "B" ? "#92400e" : "#6d28d9",
                      }}>{c.retry_tier || "A"}</span>
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>
                      {c.last_attempt || 0}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", whiteSpace: "nowrap" }}>
                      {c.next_retry ? formatDate(c.next_retry) : "—"}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => handleRetry(c.invoiceid)}
                        disabled={actionLoading === c.invoiceid}
                        style={{
                          padding: "0.3rem 0.6rem", borderRadius: "6px", border: "1px solid #635bff",
                          background: "#fff", color: "#635bff", fontSize: "0.75rem", fontWeight: 600,
                          cursor: "pointer", marginRight: "0.4rem",
                        }}
                      >
                        {actionLoading === c.invoiceid ? <i className="fas fa-spinner fa-spin"></i> : "Retry Now"}
                      </button>
                      <button
                        onClick={() => handleCancel(c.invoiceid)}
                        disabled={actionLoading === c.invoiceid}
                        style={{
                          padding: "0.3rem 0.6rem", borderRadius: "6px", border: "1px solid #ef4444",
                          background: "#fff", color: "#ef4444", fontSize: "0.75rem", fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Retry Activity */}
      <div style={card}>
        <div style={{ ...label, marginBottom: "1rem" }}>Recent Retry Activity</div>
        {history.length === 0 ? (
          <p style={{ color: "#64748b", textAlign: "center", padding: "2rem 0" }}>No retry attempts yet</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e3e8ee" }}>
                  {["Email", "Plan", "Amount", "Attempt", "Tier", "Result", "Response", "When"].map((h) => (
                    <th key={h} style={{ padding: "0.6rem 0.75rem", color: "#425466", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((a) => (
                  <tr key={a.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "0.6rem 0.75rem" }}>{a.email}</td>
                    <td style={{ padding: "0.6rem 0.75rem", whiteSpace: "nowrap" }}>
                      {a.planName} ({a.planInterval})
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600 }}>${a.price}</td>
                    <td style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>{a.attempt_number}</td>
                    <td style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", padding: "0.15rem 0.5rem", borderRadius: "4px",
                        fontSize: "0.75rem", fontWeight: 700,
                        background: a.retry_tier === "B" ? "#fef3c7" : "#ede9fe", color: a.retry_tier === "B" ? "#92400e" : "#6d28d9",
                      }}>{a.retry_tier || "A"}</span>
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      <span style={{
                        display: "inline-block", width: "8px", height: "8px", borderRadius: "50%",
                        background: attemptStatusColors[a.status] || "#94a3b8", marginRight: "0.4rem",
                      }}></span>
                      {a.status}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={`${a.response_code || ""} — ${a.response_text || ""}`}>
                      {a.response_code && <span style={{ fontWeight: 600, marginRight: "0.3rem" }}>{a.response_code}</span>}
                      {a.response_text || "—"}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", whiteSpace: "nowrap", color: "#64748b" }}>
                      {timeAgo(a.attempted_at || a.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DunningDashboard;
